import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BankingSummary } from '../types';
import bankingService from '../services/bankingService';

/**
 * Kontostände fürs Dashboard.
 *
 * Getrennt gezeigt wird, was verfügbar ist und was für die Steuern zurückliegt —
 * das ist die Zahl, die im Alltag zählt. Ohne angelegtes Konto zeigt die Karte nichts.
 */

const euro = (cents: number): string =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);

const AccountsCard: React.FC = () => {
  const [data, setData] = useState<BankingSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    bankingService.getSummary()
      .then(s => { if (!cancelled) { setData(s); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading || !data || data.accounts.length === 0) return null;

  const hasReserve = data.accounts.some(a => a.purpose !== 'OPERATING');
  const todo = data.uncategorizedCount + data.unmatchedIncomingCount;

  return (
    <div className="rounded-xl p-5 mb-6 animate-fade-up"
      style={{ background: 'var(--surface-bg)', border: '1px solid var(--surface-border)' }}>

      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
        <h2 className="text-sm font-bold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
          Konten
        </h2>
        <Link to="/banking" className="text-xs underline" style={{ color: 'var(--text-muted)' }}>
          Umsätze ansehen
        </Link>
      </div>

      <div className="flex flex-wrap items-end gap-x-10 gap-y-4">
        <div>
          <div className="text-3xl font-extrabold tracking-tight" style={{ color: 'var(--text-primary)' }}>
            {euro(data.operatingBalanceCents)}
          </div>
          <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
            verfügbar{hasReserve && ' · ohne die Rücklagen'}
          </div>
        </div>

        {hasReserve && (
          <div>
            <div className="text-2xl font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>
              {euro(data.reserveBalanceCents)}
            </div>
            <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>zurückgelegt</div>
          </div>
        )}

        <div className="nums-tabular">
          <div className="text-lg font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>
            {euro(data.incomingCents - data.outgoingCents)}
          </div>
          <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
            {data.year}: {euro(data.incomingCents)} ein, {euro(data.outgoingCents)} aus
          </div>
        </div>
      </div>

      {todo > 0 && (
        <p className="text-xs mt-4" style={{ color: 'var(--text-muted)' }}>
          {data.uncategorizedCount > 0 && `${data.uncategorizedCount} Umsätze ohne Kategorie`}
          {data.uncategorizedCount > 0 && data.unmatchedIncomingCount > 0 && ' · '}
          {data.unmatchedIncomingCount > 0 && `${data.unmatchedIncomingCount} Eingänge ohne Rechnung`}
          {' · '}
          <Link to="/banking" className="underline" style={{ color: 'var(--text-primary)' }}>zuordnen</Link>
        </p>
      )}
    </div>
  );
};

export default AccountsCard;
